import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  DesignToken,
  Heading,
  ITooltipProps,
  Icon,
  RadiusToken,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { inTrial } from "helpers/subscription";
import { NavigationItemInterface, NavigationItems } from "interfaces/navigationItem";
import { getCurrentSubscriptionDaysLeft, selectCurrentSubscription } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { NavigationItemBadge } from "./NavigationItemBadge";

interface IProps {
  item: NavigationItemInterface;
  isMobile: boolean;
  accessAllowed?: boolean;
  isIntegration?: boolean;
  onClick?: () => void;
}

const tooltipHooksConfig: Pick<ITooltipProps, "useClickHookProps" | "useDismissHookProps"> = {
  useClickHookProps: {
    ignoreMouse: true,
  },
  useDismissHookProps: {
    referencePress: true,
    referencePressEvent: "click",
  },
};

const NavigationItem: FC<IProps> = ({ item, accessAllowed, onClick, isMobile, isIntegration }) => {
  const { iconName, iconNameFilled, icon, path, name } = item;
  const subscription = useSelector(selectCurrentSubscription);
  const daysLeft = useSelector(getCurrentSubscriptionDaysLeft);

  const isTrial = inTrial(subscription);
  const isActive = path && location.pathname.startsWith(path);
  const shouldDisplayTooltip = name && accessAllowed && !isMobile;
  const shouldDisplayCounter = daysLeft <= 7 && isTrial && path && item.id === NavigationItems.Subscription;

  const handleSendEvent = (): void => {
    if (isIntegration) {
      trackEngagement("Navbar: App opened", EventPlace.Navbar, { appName: item.name });
    } else {
      trackEngagement(`Navbar: ${item.name} section opened`, EventPlace.Navbar);
    }
  };

  const rawNavItem = (
    <Container
      className={item.id === NavigationItems.News ? "news" : ""}
      data-testid={`nav-${item.id}`}
      onClick={onClick}
    >
      <NavItem isActive={!!isActive} isDisabled={!accessAllowed} onClick={handleSendEvent}>
        {iconName && iconNameFilled && <Icon size="large" source={isActive ? iconNameFilled : iconName} />}
        {isMobile && name && (
          <MobileItemTitle size="xs" isActive={!!isActive}>
            {name}
          </MobileItemTitle>
        )}
        {icon && <MarketplaceAppIcon src={icon} alt={name} />}
      </NavItem>
      <NavigationItemBadge itemId={item.id} accessAllowed={!!accessAllowed} />
    </Container>
  );

  const navItem =
    path && accessAllowed ? (
      <Link to={path} css={{ textDecoration: "none" }}>
        {rawNavItem}
      </Link>
    ) : (
      rawNavItem
    );

  if (shouldDisplayCounter) {
    return (
      <Link to={path} css={{ textDecoration: "none" }} onClick={handleSendEvent}>
        <DaysCounter data-testid={`nav-${item.id}`}>
          <Counter>{daysLeft}</Counter>
          <CounterLabel>{plural(daysLeft, "day", "days")}</CounterLabel>
        </DaysCounter>
      </Link>
    );
  }

  return shouldDisplayTooltip ? (
    <ItemTooltip
      hoverOnDelay={400}
      kind="invert"
      floatingStrategy="fixed"
      placement="right"
      triggerRenderer={navItem}
      {...tooltipHooksConfig}
    >
      {name}
    </ItemTooltip>
  ) : (
    navItem
  );
};

NavigationItem.displayName = "NavigationItem";

const activeBackground = css`
  background: color-mix(in srgb, ${withVar(DesignToken.SurfaceLockedWhite)} 15%, transparent);
`;

const Container = styled.div`
  position: relative;
`;

const NavItem = styled.div<{ isActive: boolean; isDisabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin: 0 6px;
  min-height: 42px;
  min-width: 42px;
  cursor: ${(p): string => (p.isDisabled ? "not-allowed" : "pointer")};
  border-radius: ${withVar(RadiusToken.Radius3)};
  transition: all 0.2s ease-in-out 0s;
  ${(p): SerializedStyles | string => (p.isActive ? activeBackground : "")};

  svg,
  img {
    width: ${withVar(SpacingToken.Spacing6)};
    height: ${withVar(SpacingToken.Spacing6)};
    color: ${(p): string => withVar(p.isDisabled ? DesignToken.ContentLockedDisabled : DesignToken.ContentLockedWhite)};
    opacity: ${(p): number => (p.isActive && !p.isDisabled ? 1 : 0.5)};
    scale: 0.9;
  }

  ${(p): SerializedStyles | string =>
    !p.isDisabled
      ? css`
          &:hover {
            ${activeBackground};
            svg,
            img {
              width: ${withVar(SpacingToken.Spacing6)};
              height: ${withVar(SpacingToken.Spacing6)};
              opacity: 1;
              scale: 1;
            }
          }
        `
      : ""}

  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    min-height: 48px;
    min-width: 56px;
    padding-inline: ${withVar(SpacingToken.Spacing1)};
  }
`;

const MobileItemTitle = styled(Text)<{ isActive: boolean }>`
  color: ${withVar(DesignToken.ContentLockedWhite)};
  opacity: ${(p): number => (p.isActive ? 1 : 0.5)};
`;

const DaysCounter = styled.div`
  display: flex;
  justify-content: center;
  min-height: 42px;
  min-width: 42px;
  border-radius: ${withVar(RadiusToken.Radius3)};
  cursor: pointer;
  margin: 0 6px;
  position: relative;
  background-color: ${withVar(DesignToken.SurfaceAccentEmphasisHighNegative)};

  &:hover {
    background-color: ${withVar(DesignToken.ActionNegativeHover)};
  }
`;

const Counter = styled(Heading)`
  color: ${withVar(DesignToken.ContentLockedWhite)};
  position: absolute;
  top: 2px;
`;

const CounterLabel = styled(Text)`
  color: ${withVar(DesignToken.ContentLockedWhite)};
  font-size: 10px;
  position: absolute;
  bottom: 0;
`;

const ItemTooltip = styled(Tooltip)`
  padding-top: ${withVar(SpacingToken.Spacing2)};
  padding-bottom: ${withVar(SpacingToken.Spacing2)};
`;

const MarketplaceAppIcon = styled.img`
  border-radius: ${withVar(RadiusToken.Radius1)};
  transition: all 0.2s ease-in-out 0s;
`;

export { NavigationItem, type IProps as NavigationItemProps };
